import { Box } from '@mui/material';
import { useCallback } from 'react';
import { useSearchParams } from 'react-router';

import { SectionTabs, withCount } from '~/components/common/SectionTabs';
import ToggleButtonGroup from '~/components/common/ToggleButtonGroup';
import { SpendingTable } from '~/components/spending/SpendingTable';
import { useSpending, useSpendingTableModel } from '~/data/hooks/useSpending';
import { InvoiceKind, type InvoiceWithPortfolio } from '~/data/schemas/invoice';
import { useDetailTableHeight } from '~/hooks/useDetailTableHeight';
import { buildUrlForInvoice } from '~/utils/urlBuilder';

type PropertySpendingProps = {
  propertyId: string;
  /**
   * Turns the Invoices/Proposals control into the counted subtabs the other detail pages
   * use. A count still in flight is `undefined`, which renders as no chip.
   */
  classCounts?: {
    invoices: number | undefined;
    proposals: number | undefined;
    credits: number | undefined;
  };
};

export function PropertySpending({ propertyId, classCounts }: PropertySpendingProps) {
  const tableHeight = useDetailTableHeight();
  const {
    spendingView,
    onViewChange,
    selectSpendingView,
    sortModel,
    setSortModel,
    paginationModel,
    setPaginationModel,
    filterModel,
    setFilterModel,
    spendingViewList,
  } = useSpendingTableModel();

  // A row opens the invoice in a new tab, so the property — its tab, its subtab and the
  // page of results — is still behind it.
  const [searchParams] = useSearchParams();
  const rowHref = useCallback(
    (row: InvoiceWithPortfolio) => buildUrlForInvoice(row.id, searchParams),
    [searchParams],
  );

  const { invoices, numInvoices, isLoading, onExport } = useSpending({
    propertyId,
    paginationModel,
    filterModel,
    sortModel,
    currentView: spendingView,
  });

  const table = (
    <SpendingTable
      height={tableHeight}
      invoices={invoices}
      numInvoices={numInvoices}
      isLoading={isLoading}
      onExport={onExport}
      onPaginationModelChange={setPaginationModel}
      onSortModelChange={setSortModel}
      onFilterModelChange={setFilterModel}
      paginationModel={paginationModel}
      filterModel={filterModel}
      sortModel={sortModel}
      hiddenColumns={['property', 'portfolio']}
      viewsStoreKey="invoices-table-property"
      enableQuickFilter
      canFilterProperty={false}
      rowHref={rowHref}
      enableRowPreview
      previewList="property_invoices"
    />
  );

  if (classCounts) {
    return (
      // Both subtabs render the same table: the class is a parameter of the read behind it,
      // not a different view, so returning one element from both keeps React reconciling it
      // in place rather than remounting the grid and losing its paging and sort.
      //
      // Controlled, because the selection lives in `invoice_kind` so a link can open this tab
      // on Proposals.
      <SectionTabs
        ariaLabel="Invoice, proposal and credit views"
        value={spendingView}
        onChange={selectSpendingView}
        tabs={[
          {
            value: InvoiceKind.Invoice,
            label: withCount('Invoices', classCounts.invoices),
            render: () => table,
          },
          {
            value: InvoiceKind.Proposal,
            label: withCount('Proposals', classCounts.proposals),
            render: () => table,
          },
          {
            value: InvoiceKind.Credit,
            label: withCount('Credits', classCounts.credits),
            render: () => table,
          },
        ]}
      />
    );
  }

  return (
    <Box display="flex" flexDirection="column" gap={2} width="100%">
      <Box display="flex" justifyContent="space-between">
        <ToggleButtonGroup
          activeView={spendingView}
          viewList={spendingViewList}
          handleChange={onViewChange}
        />
      </Box>
      {table}
    </Box>
  );
}
